import { StateMessage } from "@/components/StateMessage";
import { EntryRow } from "@/components/EntryRow";
import { useHomeShared } from "./HomeShared";
import "./ActivityTile.css";

/**
 * ef-061-4 — the Home "Recent activity" tile: the live entries feed and its loading / error / empty states,
 * moved out of Dashboard.tsx unchanged. The feed, the reference lookups and the payment-anomaly history are the
 * shared Home state (`HomeShared`), read once for every tile.
 */
export function ActivityTile() {
  const {
    entries, newEntryIds, vendorById, accountById, anomalyByEntryId,
    hasError, isLoading, entriesError, refError,
  } = useHomeShared();
  return (
    <section className="dashboard__feed" aria-labelledby="feed-heading">
      <div className="dashboard__feed-head">
        <h2 id="feed-heading">Recent activity</h2>
        <p className="dashboard__feed-hint">Scan the QR on the posterboard to add an entry</p>
      </div>

      {hasError && (
        <StateMessage
          tone="error"
          title="Can’t reach EasyFarm right now."
          detail={entriesError ?? refError ?? undefined}
        />
      )}

      {!hasError && isLoading && (
        <StateMessage tone="loading" title="Loading the live feed…" />
      )}

      {!hasError && !isLoading && entries.length === 0 && (
        <StateMessage
          tone="empty"
          title="No entries yet."
          detail="The first scan from the posterboard QR will show up here within a few seconds."
        />
      )}

      {!hasError && !isLoading && entries.length > 0 && (
        // Polite live region: announces the count change, not every field,
        // so a screen reader user isn't flooded every time an entry lands.
        //
        // tabIndex=0 because this list SCROLLS. A scrollable region that
        // cannot be focused is unreachable by keyboard — you can see the
        // overflow but never scroll it without a mouse. Caught by the axe
        // gate (scrollable-region-focusable, serious): a real bug, not a
        // checker being pedantic.
        <ul
          className="dashboard__feed-list"
          // Named, and not only for screen readers. Four e2e locators reached this
          // feed with getByRole("list").first(), which silently became the SIDEBAR
          // NAV when the app shell landed and put a nav list ahead of it in the DOM.
          // Those assertions kept passing against the nav — including the one whose
          // comment says it proves the dashboard is never empty at hall open. A name
          // makes the locator say which list it means.
          aria-label="Recent activity"
          aria-live="polite"
          aria-relevant="additions"
          tabIndex={0}
        >
          {entries.map((entry) => (
            <EntryRow
              key={entry.id}
              entry={entry}
              vendor={entry.vendorId ? vendorById.get(entry.vendorId) : undefined}
              account={entry.accountId ? accountById.get(entry.accountId) : undefined}
              isNew={newEntryIds.has(entry.id)}
              anomaly={anomalyByEntryId.get(entry.id) ?? null}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
